import { IDeveloperField, IRowHookInput } from 'dromo-uploader-react'
import { ITableMessage } from 'dromo-uploader-js'
import {
  IDeveloperSelectOption,
  IValidatorField,
} from 'dromo-uploader-js/dist/interfaces'
import {
  ContractType,
  DealStructure,
  FacilityType,
  SystemType,
} from '@black-bear-energy/black-bear-energy-common'
import {
  addressSchema,
  citySchema,
  stateSchema,
  zipCodeSchema,
  latSchema,
  longSchema,
  nameSchema,
  notesSchema,
} from '../helpers/DromoUploader/commonFieldSchemas'
import { RowValidations } from '../helpers/DromoUploader/updateCellMessages'
import { validateDate } from '../helpers/DromoUploader/dateValidation'
import {
  validateLength,
  validateRequired,
} from '../helpers/DromoUploader/stringValidation'
import { validateZipcode } from '../helpers/DromoUploader/zipCodeValidation'
import { validateNumberMinMax } from '../helpers/DromoUploader/numberValidation'
import { REIT } from '../../state/modules/reits'

export const missingSoldToWarning = 'No value set for "Sold To"'

/**
 * Fields which are not required, but which are important to have if data is available.
 * Missing high priority fields result in a warning on the field's cell and the row's Site Action cell.
 * Records with missing high priority fields are also moved to the top of the preview table.
 */
export const highPriorityFields = [
  'dealStructure',
  'systemType',
  'contractType',
  'onlineDate',
  'facilityType',
]

const contractTypeOptions = (
  Object.keys(ContractType) as Array<keyof typeof ContractType>
).map((key) => {
  return {
    label: ContractType[key],
    value: key,
  }
})

const dealStructureOptions = (
  Object.keys(DealStructure) as Array<keyof typeof DealStructure>
).map((key) => {
  return {
    label: DealStructure[key],
    value: key,
  }
})

const facilityTypeOptions = (
  Object.keys(FacilityType) as Array<keyof typeof FacilityType>
).map((key) => {
  return {
    label: FacilityType[key],
    value: key,
  }
})

const systemTypeOptions = (
  Object.keys(SystemType) as Array<keyof typeof SystemType>
).map((key) => {
  return {
    label: SystemType[key],
    value: key,
  }
})

export const systemSizeSchema: IDeveloperField = {
  label: 'System Size (MWdc)',
  key: 'systemSize',
  type: [
    'number',
    {
      round: 3,
    },
  ],
}

export const soldSchema: IDeveloperField = {
  label: 'Sold',
  key: 'sold',
  type: 'checkbox',
}

const soldToSchema: IDeveloperField = {
  label: 'Sold To',
  key: 'soldTo',
  type: ['select', { allowCustom: true }],
  selectOptions: [],
}

export function getSoldToSchema(
  reitList: REIT[],
  selectedReit?: number
): IDeveloperField {
  let selectedREITName: string | undefined
  const options: IDeveloperSelectOption[] = []
  for (const reit of reitList) {
    if (reit.id === selectedReit) {
      selectedREITName = reit.name
    } else {
      options.push({
        label: reit.name,
        value: reit.id.toString(),
      })
    }
  }
  // if we know the name of the REIT that owns this list,
  // disallow that REIT name as a "SoldTo" value
  const validators: IValidatorField[] =
    selectedREITName == undefined
      ? []
      : [
          {
            validate: 'regex_exclude',
            regex: new RegExp(selectedREITName),
            errorMessage: '"Sold To" cannot be the name of the current owner',
          },
        ]
  return {
    ...soldToSchema,
    selectOptions: options,
    validators,
  }
}

export const fieldSchema: IDeveloperField[] = [
  addressSchema,
  { ...notesSchema, label: 'Black Bear Notes' },
  citySchema,
  {
    label: 'Contract Type',
    key: 'contractType',
    type: 'select',
    selectOptions: contractTypeOptions,
  },
  {
    label: 'Date System Came Online',
    key: 'onlineDate',
    type: 'date',
  },
  {
    label: 'Deal Structure',
    key: 'dealStructure',
    type: 'select',
    selectOptions: dealStructureOptions,
  },
  {
    label: 'Facility Type',
    key: 'facilityType',
    type: 'select',
    selectOptions: facilityTypeOptions,
  },
  {
    label: 'Has Storage',
    key: 'hasStorage',
    type: 'checkbox',
  },
  {
    label: 'Is Operating',
    key: 'isOperating',
    type: 'checkbox',
  },
  {
    label: 'Landlord Owner',
    key: 'landlordOwner',
  },
  latSchema,
  longSchema,
  {
    label: 'REIT Notes',
    key: 'REITNotes',
  },
  nameSchema,
  soldSchema,
  stateSchema,
  systemSizeSchema,
  {
    label: 'System Type',
    key: 'systemType',
    type: 'select',
    selectOptions: systemTypeOptions,
  },
  {
    label: 'Tenant Lead',
    key: 'tenantLead',
  },
  zipCodeSchema,
  // hidden columns
  {
    label: 'Existing Record Id',
    key: 'existingRecordId',
    hidden: true,
  },
  {
    label: 'Row UUID',
    key: 'rowId',
    hidden: true,
  },
  {
    label: 'Is Geocode Valid',
    key: 'isGeocodeValid',
    hidden: true,
  },
]

export const validations: RowValidations = {
  address: (value: string) =>
    validateLength(value, 150).concat(validateRequired(value)),
  city: (value: string) =>
    validateLength(value, 150).concat(validateRequired(value)),
  onlineDate: (value: string) => validateDate(value),
  dealStructure: (value: string, row?: IRowHookInput['row']) =>
    validateDealStructureAndState(value, row),
  latitude: (value: string) =>
    // valid range for USA and Canada
    validateNumberMinMax(value, 12, 90).concat(validateRequired(value)),
  longitude: (value: string) =>
    // valid range for USA and Canada
    validateNumberMinMax(value, -170, -51).concat(validateRequired(value)),
  name: (value: string) =>
    validateLength(value, 150).concat(validateRequired(value)),
  sold: (value: string, row?: IRowHookInput['row']) =>
    validateSoldAndSoldTo(row),
  soldTo: (value: string, row?: IRowHookInput['row']) =>
    validateSoldAndSoldTo(row),
  state: (value: string) => validateRequired(value),
  systemSize: (value: string) =>
    validateNumberMinMax(value, 0.001, 25).concat(validateRequired(value)),
  zipCode: (value: string) =>
    validateZipcode(value).concat(validateRequired(value)),
}

function validateDealStructureAndState(
  dealStructureValue: string,
  row?: IRowHookInput['row']
): ITableMessage[] {
  if (!row) {
    throw new Error('No row values provided')
  }
  if (dealStructureValue !== DealStructure.CommunitySolar.toString()) {
    return []
  }
  if (row['state'].value !== 'CA') {
    return []
  }
  return [
    { message: 'Invalid Community Solar deal structure in CA', level: 'error' },
  ]
}

function validateSoldAndSoldTo(row?: IRowHookInput['row']): ITableMessage[] {
  if (!row) {
    throw new Error('No row values provided')
  }
  const isSold = row['sold'].value === 'checked'
  const soldTo = String(row['soldTo'].value)

  if (isSold && soldTo === '') {
    return [
      {
        message: missingSoldToWarning,
        level: 'warning',
      },
    ]
  }

  if (!isSold && soldTo !== '') {
    return [
      {
        message: 'Cannot have "Sold To" value if "Sold" is unchecked',
        level: 'error',
      },
    ]
  }

  return []
}
